import { Button } from "@egaranti/components";
import { useToast } from "@egaranti/components";
import { Input } from "@egaranti/components";
import { Pagination } from "@egaranti/components";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@egaranti/components";
import { TableSkeleton } from "@egaranti/components";

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import extendedWarrantyService from "@/services/extendedWarrantyService";

import SaleStatusTag from "@/components/extendedWarranty/transactions/saleStatusTag";
import CheckRole from "@/components/shared/checkRole";
import EmptyTable from "@/components/shared/EmptyTable";
import PageSizeSelect from "@/components/shared/pageSizeSelect";
import TableHeaderWrapper from "@/components/shared/tableHeaderWrapper";

// Ödemesi alınmış öneriye ikinci link gönderilemez (BE de reddediyor).
const PAID_STATUSES = ["PAID", "COMPLETION_FAILED"];

// BE hata anahtarları Locize'a girilene kadar Türkçe karşılıkları.
const ERROR_FALLBACKS = {
  "sales.candidate.plan.not.eligible": "Bu paket bu müşteriye önerilemez.",
  "sales.candidate.already.paid": "Müşteri bir öneriyi zaten ödedi.",
};

const SEARCH_DEBOUNCE_MS = 400;

const formatDate = (value) =>
  value ? value.slice(0, 10).split("-").reverse().join(".") : "-";

const formatPrice = (value) =>
  typeof value === "number"
    ? value.toLocaleString("tr-TR", { minimumFractionDigits: 2 })
    : "-";

/**
 * EPS-4976 — egaranti Plus Adaylar. Dijital garanti kayıtlarından egaranti Plus
 * alabilecek müşteriler ve onlara uygun paketler; "Öner" seçili paketin ödeme
 * linkini ek onay adımı olmadan müşteriye gönderir. Müşteri ödeyince satış
 * otomatik oluşur (Satışlar listesinde "Ödeme Bekleniyor" → satış).
 *
 * Paket önerisi BE'de: yalnızca online ödeme linkine izin veren planlar, aynı
 * kategoride elindekinden uzun paketler, toplam 3 yıl uzatılmış garanti tavanı.
 */
const Candidates = () => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [candidates, setCandidates] = useState([]);
  const [paging, setPaging] = useState({
    page: 1,
    size: Number(localStorage.getItem("pageSize")) || 10,
    totalPages: 0,
  });
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("NEWEST");
  const [loading, setLoading] = useState(true);
  const [selectedPlans, setSelectedPlans] = useState({});
  const [selectedChannels, setSelectedChannels] = useState({});
  const [sendingId, setSendingId] = useState(null);
  const firstRender = useRef(true);

  const fetchCandidates = useCallback(
    async ({ page, size, query: q, sort: s }) => {
      setLoading(true);
      try {
        const data = await extendedWarrantyService.getSalesCandidates({
          page,
          size,
          query: q?.trim(),
          sort: s,
        });
        setCandidates(data?.content || []);
        setPaging({ page, size, totalPages: data?.totalPages || 0 });
      } catch (err) {
        toast({
          title: t("common.unexpectedError", "Beklenmeyen bir hata oluştu"),
          variant: "error",
        });
      } finally {
        setLoading(false);
      }
    },
    [t, toast],
  );

  const reload = (overrides = {}) =>
    fetchCandidates({ page: paging.page, size: paging.size, query, sort, ...overrides });

  // Arama ve sıralama değişince ilk sayfaya dönülür; yazarken her harfte istek
  // atılmasın diye arama kısa bir gecikmeyle uygulanır.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      reload();
      return undefined;
    }
    const timer = setTimeout(() => reload({ page: 1 }), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, sort]);

  // Ödeme linki gönderilmiş satırda gönderilen plan seçili gelir; yoksa BE'nin
  // ilk önerisi (bir üst paket).
  const selectedPlanIdOf = (candidate) => {
    const chosen = selectedPlans[candidate.individualCustomerProductId];
    if (chosen) return String(chosen);
    const plans = candidate.recommendedPlans || [];
    const sent = plans.find((plan) => plan.planId === candidate.paymentLinkPlanId);
    return String((sent || plans[0])?.planId ?? "");
  };

  const selectedPlanOf = (candidate) =>
    (candidate.recommendedPlans || []).find(
      (plan) => String(plan.planId) === selectedPlanIdOf(candidate),
    );

  // Kanal seçimi yalnızca müşterinin hem telefonu hem e-postası varsa sorulur;
  // tek iletişim bilgisi varsa o kullanılır (varsayılan SMS).
  const hasBothChannels = (candidate) =>
    Boolean(candidate.phone) && Boolean(candidate.email);

  const channelOf = (candidate) =>
    selectedChannels[candidate.individualCustomerProductId] ||
    (candidate.phone ? "SMS" : "EMAIL");

  const handleRecommend = async (candidate) => {
    const planId = selectedPlanIdOf(candidate);
    if (!planId) return;
    setSendingId(candidate.individualCustomerProductId);
    try {
      const response = await extendedWarrantyService.recommendSalesCandidate(
        candidate.individualCustomerProductId,
        Number(planId),
        channelOf(candidate),
      );
      toast({
        title: t(
          "extendedWarranty.add.paymentLinkSent",
          "Ödeme linki gönderildi: {{target}}",
          { target: response?.maskedTarget || "" },
        ),
        variant: "success",
      });
      setSelectedPlans((prev) => {
        const next = { ...prev };
        delete next[candidate.individualCustomerProductId];
        return next;
      });
      reload();
    } catch (err) {
      const raw = err?.response?.data?.errors?.[0]?.message;
      toast({
        title: raw
          ? t(raw, ERROR_FALLBACKS[raw] || raw)
          : t("common.unexpectedError", "Beklenmeyen bir hata oluştu"),
        variant: "error",
      });
    } finally {
      setSendingId(null);
    }
  };

  const unavailableText = (reason) =>
    reason === "NO_PRODUCT_PRICE"
      ? t("candidatesPage.noProductPrice", "Ürün fiyatı tanımlı değil")
      : t("candidatesPage.noEligiblePlan", "Uygun paket yok");

  return (
    <main className="min-h-full bg-[#F9FAFC] p-4">
      <div className="rounded-lg">
        <div className="flex flex-col rounded-t-lg bg-white pb-4">
          <TableHeaderWrapper className="flex flex-wrap items-center justify-between gap-4">
            <div className="w-full max-w-[420px]">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t(
                  "candidatesPage.searchPlaceholder",
                  "Müşteri adı, telefon, e-posta, ürün ya da seri no",
                )}
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-600">
              {t("candidatesPage.sort", "Sıralama")}
              <select
                className="rounded-md border border-gray-300 p-1"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
              >
                <option value="NEWEST">
                  {t("candidatesPage.sortNewest", "En yeni kayıt")}
                </option>
                <option value="WARRANTY_EXPIRE">
                  {t(
                    "candidatesPage.sortWarrantyExpire",
                    "Garanti bitişi yaklaşan",
                  )}
                </option>
              </select>
            </label>
          </TableHeaderWrapper>
          {loading ? (
            <Table>
              <TableSkeleton colSpan={8} />
            </Table>
          ) : candidates.length ? (
            <Table>
              <TableHeader>
                <TableHead>{t("extendedWarrantiesPage.customer")}</TableHead>
                <TableHead>{t("productsPage.productName")}</TableHead>
                <TableHead>
                  {t("candidatesPage.registeredAt", "Kayıt Tarihi")}
                </TableHead>
                <TableHead>
                  {t("candidatesPage.warrantyExpireDate", "Garanti Bitiş")}
                </TableHead>
                <TableHead>
                  {t("candidatesPage.currentPackages", "Mevcut Paket")}
                </TableHead>
                <TableHead>
                  {t("candidatesPage.recommendedPackage", "Önerilecek Paket")}
                </TableHead>
                <TableHead>
                  {t("extendedWarrantiesPage.status.title", "Statü")}
                </TableHead>
                <TableHead />
              </TableHeader>
              <TableBody>
                {candidates.map((candidate) => {
                  const plans = candidate.recommendedPlans || [];
                  const selectedPlan = selectedPlanOf(candidate);
                  const alreadyPaid = PAID_STATUSES.includes(
                    candidate.paymentStatus,
                  );
                  return (
                    <TableRow key={candidate.individualCustomerProductId}>
                      <TableCell>
                        <div className="flex flex-col">
                          <span>
                            {candidate.firstName} {candidate.lastName}
                          </span>
                          <span className="text-xs text-[#86868b]">
                            {candidate.phone || candidate.email || "-"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span>{candidate.productName || "-"}</span>
                          <span className="text-xs text-[#86868b]">
                            {[candidate.brand, candidate.serialNumber]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>{formatDate(candidate.createdAt)}</TableCell>
                      <TableCell>
                        {formatDate(candidate.warrantyExpireDate)}
                      </TableCell>
                      <TableCell>
                        {candidate.currentPackageNames ||
                          t("candidatesPage.noPackage", "Yok")}
                      </TableCell>
                      <TableCell>
                        {plans.length ? (
                          <div className="flex min-w-[220px] flex-col gap-1">
                            <select
                              className="w-full rounded-md border border-gray-300 p-1 text-sm"
                              title={selectedPlan?.name}
                              value={selectedPlanIdOf(candidate)}
                              onChange={(e) =>
                                setSelectedPlans({
                                  ...selectedPlans,
                                  [candidate.individualCustomerProductId]:
                                    e.target.value,
                                })
                              }
                            >
                              {plans.map((plan) => (
                                <option key={plan.planId} value={plan.planId}>
                                  {plan.name}
                                </option>
                              ))}
                            </select>
                            {selectedPlan && (
                              <span className="text-xs text-[#86868b]">
                                {formatPrice(selectedPlan.amount)} ₺ ·{" "}
                                {selectedPlan.durationMonth}{" "}
                                {t("extendedWarrantiesPage.month", "ay")}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-[#86868b]">
                            {unavailableText(candidate.unavailableReason)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {candidate.paymentStatus ? (
                          <div className="flex flex-col gap-1">
                            <SaleStatusTag
                              sale={{ paymentStatus: candidate.paymentStatus }}
                            />
                            {candidate.paymentLinkPlanName && (
                              <span className="text-xs text-[#86868b]">
                                {candidate.paymentLinkPlanName}
                              </span>
                            )}
                          </div>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        <CheckRole section="ACTIVATIONS_SALES" action="EDIT">
                          <div className="flex items-center gap-2">
                            {hasBothChannels(candidate) && plans.length > 0 && (
                              <select
                                className="rounded-md border border-gray-300 p-1 text-sm"
                                aria-label={t("candidatesPage.channel", "Gönderim kanalı")}
                                value={channelOf(candidate)}
                                onChange={(e) =>
                                  setSelectedChannels({
                                    ...selectedChannels,
                                    [candidate.individualCustomerProductId]:
                                      e.target.value,
                                  })
                                }
                              >
                                <option value="SMS">SMS</option>
                                <option value="EMAIL">
                                  {t("candidatesPage.channelEmail", "E-posta")}
                                </option>
                              </select>
                            )}
                            <Button
                              size="sm"
                              disabled={
                                !plans.length ||
                                alreadyPaid ||
                                sendingId !== null
                              }
                              onClick={() => handleRecommend(candidate)}
                            >
                              {candidate.paymentStatus && !alreadyPaid
                                ? t("candidatesPage.recommendAgain", "Tekrar Öner")
                                : t("candidatesPage.recommend", "Öner")}
                            </Button>
                          </div>
                        </CheckRole>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <EmptyTable
              title={t("candidatesPage.emptyTitle", "Aday bulunamadı")}
              desc={
                query.trim()
                  ? t(
                      "candidatesPage.emptySearchDesc",
                      "Aramanızla eşleşen kayıt yok.",
                    )
                  : t(
                      "candidatesPage.emptyDesc",
                      "Dijital garanti kaydı olan müşterileriniz burada listelenir.",
                    )
              }
            />
          )}
          <div className="flex flex-col items-center">
            {paging.totalPages > 1 && (
              <Pagination
                totalPages={paging.totalPages}
                currentPage={paging.page}
                pageSize={paging.size}
                onPageChange={(page) => reload({ page })}
              />
            )}
            {!loading && (
              <PageSizeSelect
                onPageSizeChange={(size) => reload({ page: 1, size: Number(size) })}
                value={paging.size}
              />
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default Candidates;
